import React, { useEffect, useContext, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LoginContext } from '../../context/loginContext.js';
import { Spin, message, Typography, Button } from 'antd';
import axios from 'axios';

const { Text } = Typography;

// Interceptor de axios para loggear todas las requests
axios.interceptors.request.use(
  (config) => {
    console.log('🚀 [AXIOS REQUEST]', {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      fullURL: `${config.baseURL || ''}${config.url}`,
      data: config.data,
      headers: config.headers,
      timestamp: new Date().toISOString(),
    });
    return config;
  },
  (error) => {
    console.error('❌ [AXIOS REQUEST ERROR]', error);
    return Promise.reject(error);
  }
);

// Interceptor de axios para loggear todas las responses
// Guardar el ID del interceptor para poder removerlo después
let responseInterceptorId = null;

// Función para configurar el interceptor que suprime errores 403 durante el login
const setupResponseInterceptor = () => {
  // Remover interceptor anterior si existe
  if (responseInterceptorId !== null) {
    axios.interceptors.response.eject(responseInterceptorId);
  }
  
  responseInterceptorId = axios.interceptors.response.use(
    (response) => {
      console.log('✅ [AXIOS RESPONSE]', {
        status: response.status,
        statusText: response.statusText,
        url: response.config.url,
        data: response.data,
        timestamp: new Date().toISOString(),
      });
      return response;
    },
    (error) => {
      // Verificar si estamos en proceso de autenticación
      const isProcessingAuth = sessionStorage.getItem('auth0Processing') === 'true' ||
                              sessionStorage.getItem('justLoggedIn') === 'true' || 
                              window.location.pathname.includes('/auth0-callback') ||
                              window.location.hash.includes('/auth0-callback');
      
      // Si es un error 403 y estamos procesando autenticación, suprimir el error
      if (error.response?.status === 403 && isProcessingAuth) {
        console.warn('⚠️ [AXIOS] Error 403 suprimido durante autenticación:', {
          url: error.config?.url,
          timestamp: new Date().toISOString(),
        });
        // Retornar una respuesta vacía en lugar de rechazar la promesa
        // Esto evita que se muestre el error al usuario
        return Promise.resolve({ 
          status: 403, 
          data: null, 
          config: error.config 
        });
      }
      
      console.error('❌ [AXIOS RESPONSE ERROR]', {
        message: error.message,
        code: error.code,
        response: error.response ? {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
        } : null,
        request: error.request ? {
          url: error.config?.url,
          method: error.config?.method,
        } : null,
        timestamp: new Date().toISOString(),
      });
      return Promise.reject(error);
    }
  );
};

// El interceptor se configurará cuando el componente se monte

// Función helper para validar usuario (copiada del contexto)
function isValidUser(u) {
  if (!u || typeof u !== 'object') return false;
  const hasId = Number.isInteger(Number(u?.id)) || Number.isInteger(Number(u?.usu_id));
  const hasTipo = Number.isInteger(Number(u?.usu_tipousuario));
  return hasId && hasTipo;
}

const Auth0Callback = () => {
  const [searchParams] = useSearchParams();
  const { login } = useContext(LoginContext);
  const navigate = useNavigate();
  const [debugInfo, setDebugInfo] = useState({
    step: 'Iniciando...',
    data: null,
    error: null,
  });
  const [isProcessing, setIsProcessing] = useState(true);
  
  // Configurar el interceptor al montar el componente y limpiarlo al desmontar
  useEffect(() => {
    // Marcar que estamos procesando autenticación desde el inicio
    sessionStorage.setItem('auth0Processing', 'true');
    
    // Configurar el interceptor si no está configurado
    if (responseInterceptorId === null) {
      setupResponseInterceptor();
    }
    
    return () => {
      // Limpiar el flag cuando el componente se desmonte
      sessionStorage.removeItem('auth0Processing');
      
      // Limpiar el interceptor después de un delay para permitir que se completen las solicitudes
      setTimeout(() => {
        if (responseInterceptorId !== null) {
          axios.interceptors.response.eject(responseInterceptorId);
          responseInterceptorId = null;
        }
      }, 3000);
    };
  }, []);

  // Log cuando el componente se monta
  useEffect(() => {
    console.log('═══════════════════════════════════════════════════════════');
    console.log('🎯 [Auth0Callback] COMPONENTE MONTADO');
    console.log('   Timestamp:', new Date().toISOString());
    console.log('   window.location.href:', window.location.href);
    console.log('   window.location.pathname:', window.location.pathname);
    console.log('   window.location.search:', window.location.search);
    console.log('   window.location.hash:', window.location.hash);
    console.log('═══════════════════════════════════════════════════════════');
  }, []);

  useEffect(() => {
    console.log('🔍 [Auth0Callback] useEffect ejecutado');
    
    const handleCallback = async () => {
      try {
        console.log('🔍 [Auth0Callback] handleCallback iniciado');
        setDebugInfo({ step: '1. Leyendo código de Auth0...', data: null, error: null });
        
        // Auth0 redirige a /auth0-callback?code=xxx (sin hash cuando usamos HashRouter)
        // Necesitamos leer el código de los query params normales primero
        
        const urlInfo = {
          href: window.location.href,
          pathname: window.location.pathname,
          search: window.location.search,
          hash: window.location.hash,
        };
        
        setDebugInfo(prev => ({ 
          ...prev, 
          step: '1. URL recibida',
          data: { urlInfo }
        }));
        
        // Log completo de la URL para debug
        console.log('🔍 URL completa:', window.location.href);
        console.log('🔍 window.location.pathname:', window.location.pathname);
        console.log('🔍 window.location.search:', window.location.search);
        console.log('🔍 window.location.hash:', window.location.hash);
      
      // PRIMERO: Leer de window.location.search (query params normales)
      // Esto es lo que Auth0 usa cuando redirige sin hash
      const urlParams = new URLSearchParams(window.location.search);
      let code = urlParams.get('code');
      let error = urlParams.get('error');
      
      console.log('🔍 [1] Código de window.location.search:', code);
      console.log('🔍 [1] Error de window.location.search:', error);
      
      // SEGUNDO: Si no está en query params normales, intentar desde searchParams (HashRouter)
      if (!code && !error) {
        code = searchParams.get('code');
        error = searchParams.get('error');
        console.log('🔍 [2] Código de searchParams:', code);
        console.log('🔍 [2] Error de searchParams:', error);
      }
      
      // TERCERO: Si no está en query params, intentar leer del hash
      // El hash puede ser: #/auth0-callback?code=xxx o #code=xxx
      if (!code && !error) {
        const hash = window.location.hash;
        console.log('🔍 [3] Hash completo:', hash);
        
        if (hash) {
          // Caso 1: Hash con query params después de la ruta: #/auth0-callback?code=xxx
          const hashWithQuery = hash.match(/\?([^#]+)/);
          if (hashWithQuery) {
            const hashParams = new URLSearchParams(hashWithQuery[1]);
            code = code || hashParams.get('code');
            error = error || hashParams.get('error');
            console.log('🔍 [3a] Código del hash (query params):', code);
          }
          
          // Caso 2: Hash con fragment: #code=xxx
          if (!code && !error && hash.includes('code=')) {
            const hashPart = hash.substring(1); // Remover el # inicial
            const hashParams = new URLSearchParams(hashPart);
            code = code || hashParams.get('code');
            error = error || hashParams.get('error');
            console.log('🔍 [3b] Código del hash (directo):', code);
          }
        }
      }

      if (error) {
        console.error('❌ Error de Auth0:', error);
        setDebugInfo({ 
          step: 'ERROR: Error de Auth0', 
          data: { error }, 
          error: `Error en autenticación: ${error}` 
        });
        message.error(`Error en autenticación: ${error}`);
        setTimeout(() => navigate('/login'), 5000);
        return;
      }

      if (!code) {
        console.error('❌ No se recibió código de autorización de Auth0');
        console.error('❌ URL completa:', window.location.href);
        setDebugInfo({ 
          step: 'ERROR: No se recibió código', 
          data: { urlInfo }, 
          error: 'No se recibió código de autorización de Auth0' 
        });
        message.error('No se recibió código de autorización. Por favor, intenta nuevamente.');
        setTimeout(() => navigate('/login'), 5000);
        return;
      }
      
      console.log('✅ Código recibido de Auth0:', code);
      setDebugInfo(prev => ({ 
        ...prev, 
        step: '2. Código recibido de Auth0',
        data: { ...prev.data, code: code.substring(0, 20) + '...' }
      }));

      // Continuar con el intercambio de código por token
      setDebugInfo(prev => ({ ...prev, step: '3. Intercambiando código por token con Auth0...' }));
      
      // Intercambiar código por token con Auth0
      const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN;
        const auth0ClientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
        const auth0RedirectUri = process.env.REACT_APP_AUTH0_REDIRECT_URI || window.location.origin;

      setDebugInfo(prev => ({ 
        ...prev, 
        step: '3. Configuración de Auth0',
        data: { 
          ...prev.data, 
          auth0Config: {
            domain: auth0Domain ? 'Configurado' : 'NO CONFIGURADO',
            clientId: auth0ClientId ? 'Configurado' : 'NO CONFIGURADO',
            redirectUri: auth0RedirectUri,
          }
        }
      }));

      if (!auth0Domain || !auth0ClientId) {
        setDebugInfo({ 
          step: 'ERROR: Auth0 no configurado', 
          data: { auth0Domain, auth0ClientId }, 
          error: 'Auth0 no está configurado' 
        });
        message.error('Auth0 no está configurado');
        setTimeout(() => navigate('/login'), 5000);
        return;
      }

      // El redirect_uri debe ser el mismo que se usó en la autorización (sin hash)
      // Auth0 redirige a http://localhost:3001?code=xxx y luego Auth0RootHandler lo maneja
      const redirectUri = auth0RedirectUri; // http://localhost:3001 (sin hash)
      
      console.log('🔍 Redirect URI para intercambio de token:', redirectUri);
      
      let tokenResponse;
      let userInfo;
      
      try {
        // Intercambiar código por token
        tokenResponse = await axios.post(
            `https://${auth0Domain}/oauth/token`,
            {
              grant_type: 'authorization_code',
              client_id: auth0ClientId,
              code: code,
              redirect_uri: redirectUri,
            },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        // Validar que la respuesta tenga datos
        if (!tokenResponse || !tokenResponse.data) {
          console.error('❌ Respuesta de Auth0 inválida:', tokenResponse);
          setDebugInfo({ 
            step: 'ERROR: Respuesta de Auth0 inválida', 
            data: { tokenResponse }, 
            error: 'No se recibieron datos de Auth0. Por favor, intenta nuevamente.' 
          });
          setTimeout(() => navigate('/login'), 5000);
          return;
        }

        const { access_token, id_token } = tokenResponse.data;

        // Validar que los tokens estén presentes
        if (!access_token) {
          console.error('❌ No se recibió access_token de Auth0');
          setDebugInfo({ 
            step: 'ERROR: No se recibió access_token', 
            data: { tokenResponse: tokenResponse.data }, 
            error: 'No se recibió el token de acceso de Auth0. Por favor, intenta nuevamente.' 
          });
          message.error('Error al obtener el token de acceso. Por favor, intenta nuevamente.');
          setTimeout(() => navigate('/login'), 5000);
          return;
        }

        // Obtener información del usuario desde Auth0
        const userInfoResponse = await axios.get(
          `https://${auth0Domain}/userinfo`,
          {
            headers: {
              Authorization: `Bearer ${access_token}`,
            },
          }
        );

        // Validar que la respuesta tenga datos
        if (!userInfoResponse || !userInfoResponse.data) {
          console.error('❌ Respuesta de userinfo de Auth0 inválida:', userInfoResponse);
          setDebugInfo({ 
            step: 'ERROR: Respuesta de userinfo inválida', 
            data: { userInfoResponse }, 
            error: 'No se recibieron datos del usuario de Auth0. Por favor, intenta nuevamente.' 
          });
          message.error('Error al obtener la información del usuario. Por favor, intenta nuevamente.');
          setTimeout(() => navigate('/login'), 5000);
          return;
        }

        userInfo = userInfoResponse.data;
      } catch (auth0Error) {
        console.error('❌ Error al comunicarse con Auth0:', auth0Error);
        const errorMessage = auth0Error.response?.data?.error_description || 
                            auth0Error.response?.data?.error || 
                            auth0Error.message || 
                            'Error al comunicarse con Auth0';
        setDebugInfo({ 
          step: 'ERROR: Error de Auth0', 
          data: { 
            error: auth0Error.response?.data,
            status: auth0Error.response?.status,
            message: auth0Error.message
          }, 
          error: errorMessage
        });
        message.error(`Error de autenticación: ${errorMessage}`);
        setTimeout(() => navigate('/login'), 5000);
        return;
      }
      
      console.log('📧 Información del usuario de Auth0:', {
        email: userInfo.email,
        sub: userInfo.sub,
        name: userInfo.name,
        given_name: userInfo.given_name,
        family_name: userInfo.family_name,
      });

      setDebugInfo(prev => ({ 
        ...prev, 
        step: '4. Información de Auth0 obtenida',
        data: { 
          ...prev.data, 
          auth0UserInfo: {
            email: userInfo.email,
            sub: userInfo.sub,
            name: userInfo.name,
            given_name: userInfo.given_name,
            family_name: userInfo.family_name,
          }
        }
      }));

      // Verificar que el email esté presente
      if (!userInfo.email) {
        console.error('❌ No se recibió email de Auth0');
        setDebugInfo({ 
          step: 'ERROR: No hay email de Auth0', 
          data: { userInfo }, 
          error: 'No se pudo obtener el email de la cuenta' 
        });
        message.error('No se pudo obtener el email de la cuenta. Por favor, verifica tu configuración de Auth0.');
        setTimeout(() => navigate('/login'), 5000);
        return;
      }

      // Autenticar con nuestro backend usando el email de Auth0
      setDebugInfo(prev => ({ 
        ...prev, 
        step: '5. Enviando email al backend...',
        data: { 
          ...prev.data, 
          backendRequest: {
            email: userInfo.email.trim().toLowerCase(),
            apiUrl: process.env.REACT_APP_API_URL || 'http://localhost:3000/api',
          }
        }
      }));
      
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
      const emailToSend = userInfo.email.trim().toLowerCase();
      
      console.log('🔍 Buscando alumno con email:', userInfo.email);
      console.log('🔍 API URL:', apiUrl);
      console.log('🔍 Email que se enviará:', emailToSend);
      console.log('🔍 URL completa del request:', `${apiUrl}/usuarios/auth0-login`);
      
      setDebugInfo(prev => ({ 
        ...prev, 
        step: '5. Enviando request al backend...',
        data: { 
          ...prev.data, 
          backendRequest: {
            email: emailToSend,
            apiUrl: apiUrl,
            endpoint: `${apiUrl}/usuarios/auth0-login`,
            requestBody: {
              email: emailToSend,
              auth0Id: userInfo.sub,
              nombre: userInfo.name || userInfo.given_name,
              apellido: userInfo.family_name || '',
            }
          }
        }
      }));
      
      try {
          const requestUrl = `${apiUrl}/usuarios/auth0-login`;
          const requestBody = {
            email: userInfo.email.trim().toLowerCase(), // Normalizar el email
            auth0Id: userInfo.sub,
            nombre: userInfo.name || userInfo.given_name,
            apellido: userInfo.family_name || '',
            picture: userInfo.picture || null, // Incluir la foto de Auth0
          };
          
          console.log('🚀 [FRONTEND] Enviando request al backend:');
          console.log('   URL:', requestUrl);
          console.log('   Body:', JSON.stringify(requestBody, null, 2));
          console.log('   Timestamp:', new Date().toISOString());
          
          setDebugInfo(prev => ({ 
            ...prev, 
            step: '5. Enviando request al backend (axios.post)...',
            data: { 
              ...prev.data, 
              backendRequest: {
                ...prev.data.backendRequest,
                timestamp: new Date().toISOString(),
                axiosConfig: {
                  url: requestUrl,
                  method: 'POST',
                  withCredentials: true,
                }
              }
            }
          }));
          
          const backendResponse = await axios.post(
            requestUrl,
            requestBody,
            { 
              withCredentials: true,
              timeout: 10000, // 10 segundos de timeout
            }
          );
          
          console.log('✅ [FRONTEND] Respuesta recibida del backend');
          console.log('   Status:', backendResponse.status);
          console.log('   Headers:', backendResponse.headers);
          
          console.log('✅ Respuesta del backend recibida');
          console.log('📦 Respuesta completa:', JSON.stringify(backendResponse.data, null, 2));

          const { usuario, token, refreshToken } = backendResponse.data;
          
          setDebugInfo(prev => ({ 
            ...prev, 
            step: '6. Respuesta del backend recibida',
            data: { 
              ...prev.data, 
              backendResponse: {
                usuario: usuario,
                tieneToken: !!token,
                tieneRefreshToken: !!refreshToken,
                tokenLength: token ? token.length : 0,
                refreshTokenLength: refreshToken ? refreshToken.length : 0,
              }
            }
          }));

          // Verificar que sea alumno
          if (usuario.tiu_descripcion !== 'ALUMNO') {
            message.error('Este login es solo para alumnos');
            navigate('/login');
            return;
          }

          // Preparar datos del usuario para el login
          // El backend devuelve: { usu_id, usu_usuario, usu_tipousuario, tiu_descripcion }
          const userData = {
            usu_id: usuario.usu_id,
            usu_usuario: usuario.usu_usuario,
            usu_tipousuario: usuario.usu_tipousuario,
            tiu_descripcion: usuario.tiu_descripcion,
            token: token,
            refreshToken: refreshToken, // Incluir refreshToken en userData
            id: usuario.usu_id, // Agregar id para compatibilidad
          };

          console.log('🔐 Datos del usuario para login:', userData);
          console.log('🔐 Validación isValidUser:', {
            hasId: Number.isInteger(Number(userData.id)) || Number.isInteger(Number(userData.usu_id)),
            hasTipo: Number.isInteger(Number(userData.usu_tipousuario)),
            usu_id: userData.usu_id,
            usu_tipousuario: userData.usu_tipousuario,
          });
          
          // IMPORTANTE: Establecer el flag ANTES de guardar el usuario
          // Esto previene que el evento visibilitychange haga logout
          sessionStorage.setItem('justLoggedIn', 'true');
          console.log('✅ Flag justLoggedIn establecido');
          
          // Guardar refreshToken primero
          if (refreshToken) {
            try {
              localStorage.setItem('refreshToken', refreshToken);
              console.log('✅ RefreshToken guardado en localStorage');
            } catch (err) {
              console.warn('⚠️ No se pudo guardar refreshToken:', err);
            }
          }
          
          // Guardar usuario en localStorage ANTES de llamar a login
          try {
            localStorage.setItem('user', JSON.stringify(userData));
            console.log('✅ Usuario guardado en localStorage ANTES de login()');
          } catch (err) {
            console.error('❌ Error al guardar usuario:', err);
          }
          
          // Pequeño delay para asegurar que el localStorage se haya guardado
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Llamar a login y esperar a que se complete
          await login(userData);
          
          // Otro pequeño delay para asegurar que el contexto se actualice
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Verificar que el usuario se guardó correctamente
          const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
          console.log('🔐 Usuario guardado en storage:', storedUser);
          
          setDebugInfo(prev => ({ 
            ...prev, 
            step: '7. Verificando usuario guardado...',
            data: { 
              ...prev.data, 
              storedUser: storedUser,
              isValid: storedUser ? isValidUser(storedUser) : false,
            }
          }));

          if (storedUser && isValidUser(storedUser)) {
            setIsProcessing(false); // Marcar que el procesamiento fue exitoso
            setDebugInfo(prev => ({ 
              ...prev, 
              step: '✅ ÉXITO: Usuario guardado correctamente, redirigiendo...',
              data: { 
                ...prev.data, 
                finalStatus: 'success',
                redirectTo: '/home-alumno'
              }
            }));
            message.success('Inicio de sesión exitoso');
            // Marcar que acabamos de hacer login para evitar que el contexto haga logout
            sessionStorage.setItem('justLoggedIn', 'true');
            // Limpiar el flag de procesamiento
            sessionStorage.removeItem('auth0Processing');
            
            // Verificar si necesita completar documento
            let destino = '/#/home-alumno';
            try {
              const perfilRes = await fetch(`${apiUrl}/mi/perfil`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              if (perfilRes.ok) {
                const alumno = await perfilRes.json();
                if (!alumno.alu_documento) {
                  destino = '/#/completar-documento';
                }
              }
            } catch (e) {
              console.warn('No se pudo verificar documento:', e);
            }

            setTimeout(() => {
              window.location.href = destino;
            }, 300);
          } else {
            console.error('❌ Usuario no válido después de guardar:', storedUser);
            setDebugInfo(prev => ({ 
              ...prev, 
              step: '❌ ERROR: Usuario no válido después de guardar',
              data: { 
                ...prev.data, 
                storedUser: storedUser,
                isValid: storedUser ? isValidUser(storedUser) : false,
              },
              error: 'Usuario no válido después de guardar'
            }));
            message.error('Error al guardar la sesión. Por favor, intenta nuevamente.');
            setTimeout(() => navigate('/login'), 5000);
          }
        } catch (backendError) {
          setIsProcessing(false); // Marcar que el procesamiento terminó (con error)
          sessionStorage.removeItem('auth0Processing'); // Limpiar el flag de procesamiento
          console.error('❌ Error al autenticar con backend:', backendError);
          console.error('❌ Tipo de error:', backendError.name);
          console.error('❌ Mensaje de error:', backendError.message);
          console.error('❌ Email buscado:', userInfo.email);
          console.error('❌ URL intentada:', `${apiUrl}/usuarios/auth0-login`);
          console.error('❌ Status code:', backendError.response?.status);
          console.error('❌ Response data:', backendError.response?.data);
          console.error('❌ Request config:', backendError.config);
          
          const errorData = {
            name: backendError.name,
            message: backendError.message,
            status: backendError.response?.status,
            statusText: backendError.response?.statusText,
            responseMessage: backendError.response?.data?.message,
            responseData: backendError.response?.data,
            emailBuscado: userInfo.email,
            urlIntentada: `${apiUrl}/usuarios/auth0-login`,
            code: backendError.code, // Para errores de red (ECONNREFUSED, etc.)
          };
          
          setDebugInfo(prev => ({ 
            ...prev, 
            step: '❌ ERROR: Error al autenticar con backend',
            data: { 
              ...prev.data, 
              backendError: errorData
            },
            error: backendError.response?.data?.message || backendError.message || 'Error al autenticar con el sistema'
          }));
          
          // Si el usuario no existe, mostrar el mensaje del backend
          if (backendError.response?.status === 404) {
            const errorMessage = backendError.response?.data?.message || 'No se encontró el correo como alumno';
            console.error('❌ Alumno no encontrado con email:', userInfo.email);
            message.error(`${errorMessage}. Email buscado: ${userInfo.email}`);
            setTimeout(() => navigate('/login'), 5000);
          } else if (backendError.code === 'ECONNREFUSED' || backendError.message.includes('Network Error')) {
            const errorMsg = `No se pudo conectar al servidor. Verifica que el backend esté corriendo en ${apiUrl}`;
            console.error('❌ Error de conexión:', errorMsg);
            message.error(errorMsg);
            setTimeout(() => navigate('/login'), 5000);
          } else {
            const errorMsg = backendError.response?.data?.message || backendError.message || 'Error al autenticar con el sistema';
            console.error('❌ Error del backend:', errorMsg);
            message.error(errorMsg);
            setTimeout(() => navigate('/login'), 5000);
          }
        }
      } catch (error) {
        setIsProcessing(false); // Marcar que el procesamiento terminó (con error)
        sessionStorage.removeItem('auth0Processing'); // Limpiar el flag de procesamiento
        console.error('═══════════════════════════════════════════════════════════');
        console.error('❌ [Auth0Callback] ERROR GENERAL EN handleCallback');
        console.error('   Error:', error);
        console.error('   Error message:', error.message);
        console.error('   Error stack:', error.stack);
        console.error('   Timestamp:', new Date().toISOString());
        console.error('═══════════════════════════════════════════════════════════');
        
        setDebugInfo(prev => ({ 
          ...prev, 
          step: '❌ ERROR GENERAL',
          error: error.message || 'Error desconocido',
          data: { 
            ...prev.data, 
            errorDetails: {
              message: error.message,
              stack: error.stack,
              name: error.name,
            }
          }
        }));
        
        message.error('Error al procesar la autenticación: ' + (error.message || 'Error desconocido'));
        setTimeout(() => navigate('/login'), 5000);
      }
    };

    handleCallback().catch(err => {
      console.error('❌ [Auth0Callback] Error no capturado en handleCallback:', err);
    });
  }, [searchParams, login, navigate]);

  // Si hay un error Y el procesamiento terminó (no estamos en proceso), mostrar mensaje de error
  // Esto evita mostrar errores temporales durante el proceso de autenticación
  if (debugInfo.error && !isProcessing) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: '#1a1a1a',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <Text type="danger" style={{ fontSize: '16px' }}>{debugInfo.error}</Text>
        <Button type="primary" onClick={() => navigate('/login')}>
          Volver al Login
        </Button>
      </div>
    );
  }

  // Mostrar loader simple mientras procesa
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: '#1a1a1a',
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      <Spin size="large" tip="Procesando autenticación..." />
      <Text style={{ color: '#e0e0e0' }}>Por favor, espera...</Text>
    </div>
  );
};

export default Auth0Callback;

